import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnInit,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    viewChildren,
    WritableSignal
} from "@angular/core";
import {onEach} from '../../utils/observable-utils';
import {MenuModel} from "../menu.model";
import {debounceTime} from "rxjs";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonProminence} from "../../buttons/button/button";
import {LinkComponent} from "../../buttons/link/link.component";
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {MenuItemPanelComponent} from "../menu-item-panel/menu-item-panel.component";
import {ImageCacheDirective} from '../../ui-elements/image/image-cache.directive';
import {HtmlPipe} from '../../utils/html.pipe';
import {CssVarDirective} from '../../utils/css-var.directive';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    selector: "woa-menu-item-selection-panel",
    templateUrl: "./menu-item-selection-panel.component.html",
    styleUrl: "./menu-item-selection-panel.component.scss",
    imports: [
        ButtonComponent,
        CssVarDirective,
        HtmlPipe,
        ImageCacheDirective,
        LinkComponent,
        MenuItemPanelComponent,
        NgClass,
        ResponsiveDirective,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuItemSelectionPanelComponent implements OnInit {
    private destroyRef: DestroyRef = inject(DestroyRef);
    public menuModel: MenuModel = inject(MenuModel);

    ButtonProminence = ButtonProminence;

    private static readonly MIN_ITEM_WIDTH: number = 375;
    private static readonly ITEMS_SCROLLING_COMPLETE_TIMEOUT_MS: number = 250;

    public readonly pageScroller: InputSignal<ElementRef> = input.required<ElementRef>();

    public readonly showBackButton: InputSignal<boolean> = input<boolean>(true);
    public readonly showClosePromoAction: InputSignal<boolean> = input<boolean>(false);
    public readonly alwaysShowCategoryHeader: InputSignal<boolean> = input<boolean>(false);
    public readonly anchorPanel: InputSignal<ElementRef | undefined> = input<ElementRef | undefined>();
    public readonly anchorPanelTopMargin: InputSignal<number> = input<number>(0);

    public onBack: OutputEmitterRef<void> = output<void>();

    private readonly anchorTargets: Signal<ReadonlyArray<ElementRef>> = viewChildren("anchorTarget");

    public readonly anchorTargetHasBeenVisible: WritableSignal<boolean[]> = signal<boolean[]>([]);
    public readonly anchorPanelHeight: WritableSignal<number> = signal<number>(0);

    public readonly showMainCategory: Signal<boolean> = computed<boolean>(() =>
        this.alwaysShowCategoryHeader() || this.menuModel.selectedCategoryHasMainCategoryItems());

    public readonly showMainCategoryHeader: Signal<boolean> = computed<boolean>(() =>
        this.alwaysShowCategoryHeader() ||
        (this.menuModel.selectedCategoryHasMainCategoryItems() && (!!this.menuModel.selectedCategorySubcategories().length || !this.menuModel.selectedCategory()?.id)));

    public readonly showMainCategoryImage: Signal<boolean> = computed<boolean>(() =>
        this.menuModel.showSubcategoryImages() &&
        (!this.menuModel.showCategoryImages() || (!this.menuModel.selectedCategory()?.id && !this.alwaysShowCategoryHeader())) && !!this.menuModel.getCategoryImage()(this.menuModel.selectedCategory()));

    private anchorTargetObserver: IntersectionObserver;
    private anchorTargetVisible: boolean[] = [];
    private observedAnchorTargets: ElementRef[];
    private scrollingToSelectedAnchorTarget: boolean = false;

    constructor() {
        effect(() => this.setupAnchorTargetObserver());
    }

    ngOnInit(): void {
        onEach(this.menuModel.anchorSelected$, (direct: boolean) => {
            if (direct) {
                this.scrollToSelectedAnchorTarget();
            }
        }, this.destroyRef);

        onEach(this.menuModel.itemsScrolling$.pipe(
            debounceTime(MenuItemSelectionPanelComponent.ITEMS_SCROLLING_COMPLETE_TIMEOUT_MS)
        ), () => this.checkScrollingComplete(), this.destroyRef);
    }

    private setupAnchorTargetObserver(): void {
        if (this.anchorTargetObserver) {
            this.anchorTargetObserver.disconnect();
        }
        const anchorPanelHeight: number = this.anchorPanel()?.nativeElement.offsetHeight ?? 0;
        if (this.anchorTargets().length && this.anchorPanel() && anchorPanelHeight) {
            this.anchorPanelHeight.set(anchorPanelHeight);
            this.anchorTargetObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => this.trackAnchorTargetIntersections(entries), {
                root: this.pageScroller()?.nativeElement,
                rootMargin: "-" + ((this.anchorPanelTopMargin() ?? 0) + anchorPanelHeight - 10) + "px 0px 0px 0px",
                threshold: [0, 0.25, 0.5, 0.75, 1]
            });
            this.observeAnchorTargets();
        }
    }

    private observeAnchorTargets(): void {
        if (this.anchorTargetObserver) {
            this.observedAnchorTargets = [...this.anchorTargets()];
            if (this.showMainCategory() && !this.menuModel.selectedCategoryHasMainCategoryItems()) {
                this.observedAnchorTargets.shift();
            }
            this.anchorTargetObserver.disconnect();
            this.observedAnchorTargets.forEach((anchor: ElementRef) => this.anchorTargetObserver.observe(anchor.nativeElement));
        }
    }

    private trackAnchorTargetIntersections(entries: IntersectionObserverEntry[]) {
        entries.forEach((entry: IntersectionObserverEntry) => {
            this.observedAnchorTargets.some((anchor: ElementRef, index: number) => {
                if (anchor.nativeElement === entry.target) {
                    this.anchorTargetVisible[index] = entry.isIntersecting;

                    // Lazy initialisation of menu items
                    if (entry.isIntersecting && !this.anchorTargetHasBeenVisible()[index]) {
                        const updated: boolean[] = [...this.anchorTargetHasBeenVisible()];
                        updated[index] = true;
                        this.anchorTargetHasBeenVisible.set(updated);
                    }
                    return true;
                }
                return false;
            });
        });
        this.menuModel.itemsScrolling$.next();

        // We want to auto-scroll the anchor bar in response to the user manually scrolling the items.
        // However, when the user selects and anchor, this will both directly select the anchor and also trigger scrolling of the items.
        // We don't want to select intermediate anchors during this scrolling, so there is a timeout here after user selection of the anchor,
        // before which item scrolling will trigger anchor selection.
        if (this.menuModel.canAutoScrollAnchorBar) {
            this.observedAnchorTargets.some((anchor: ElementRef, anchorIndex: number) => {
                if (this.anchorTargetVisible[anchorIndex]) {
                    this.menuModel.selectAnchor(anchorIndex, true);
                    return true;
                }
                return false;
            });
        }
    }

    private get selectedAnchorTarget(): ElementRef {
        return this.menuModel.selectedAnchorIndex() === undefined || !this.observedAnchorTargets ? undefined : this.observedAnchorTargets[this.menuModel.selectedAnchorIndex()];
    }

    private scrollToSelectedAnchorTarget(): void {
        if (this.selectedAnchorTarget) {
            this.scrollingToSelectedAnchorTarget = true;
            this.selectedAnchorTarget.nativeElement.scrollIntoView({
                block: "start",
                inline: "nearest",
                behavior: "smooth"
            });
        }
    }

    private checkScrollingComplete(): void {

        // itemsScrollingStopped$ will fire when we stop receiving itemScrolling$ events due to observed intersection changes
        // for a period of ITEMS_SCROLLING_COMPLETE_TIMEOUT_MS.
        // Unfortunately, this may not always be sufficient to determine that scrolling is not still happening,
        // depending upon the number of items in each subcategory and the speed at which the browser actually scrolls.
        // For this reason, we also check whether the expected anchor target is actually in view before starting to scroll the anchor bar.
        // Note that scrolling the anchor bar stops a programmatically initiated scroll of the items.
        if (this.scrollingToSelectedAnchorTarget && this.selectedAnchorTarget) {
            if (!this.anchorTargetVisible[this.menuModel.selectedAnchorIndex()]) {
                return;
            }
            this.scrollingToSelectedAnchorTarget = false;
        }

        // Notify the anchor bar to scroll once scrolling of the menu items has actually completed.
        this.menuModel.scrollAnchorBar$.next(this.menuModel.selectedAnchorIndex());
    }

    itemColumns(items: HTMLElement): number {
        return Math.floor(items.clientWidth / MenuItemSelectionPanelComponent.MIN_ITEM_WIDTH);
    }
}
